import { Debug } from "./debug.mjs";
import EventEmitter from "eventemitter3";
import React, { createContext, useEffect, useRef } from "react";
//#region src/renderer/lib/panning.utils.ts
var Panning = class {
	instance;
	active = false;
	enabled = true;
	x = 0;
	y = 0;
	constructor(instance) {
		this.instance = instance;
	}
	setEnabled(enabled) {
		if (enabled === this.enabled) return;
		this.enabled = enabled;
		this.active = false;
		this.instance.update();
	}
	/**
	* trigger panning start
	* @param x clientX
	* @param y clientY
	*/
	start(x, y) {
		this.active = true;
		this.x = x;
		this.y = y;
	}
	/**
	* handle moving
	* @param x clientX
	* @param y clientY
	*/
	move(x, y) {
		const deltaX = (x - this.x) / this.instance.transform.scale;
		const deltaY = (y - this.y) / this.instance.transform.scale;
		const newX = this.instance.transform.x + deltaX;
		const newY = this.instance.transform.y + deltaY;
		this.instance.$move.style.setProperty("transform", `translate(${newX}px, ${newY}px)`);
	}
	/**
	* save the final change
	* @param x clientX
	* @param y clientY
	*/
	end(x, y) {
		this.active = false;
		const deltaX = (x - this.x) / this.instance.transform.scale;
		const deltaY = (y - this.y) / this.instance.transform.scale;
		const newX = this.instance.transform.x + deltaX;
		const newY = this.instance.transform.y + deltaY;
		this.instance.transform.x = newX;
		this.instance.transform.y = newY;
		this.instance.update();
	}
};
//#endregion
//#region src/renderer/lib/zoom.utils.ts
function checkZoomBounds(zoom, minScale, maxScale) {
	if (!Number.isNaN(maxScale) && zoom >= maxScale) return maxScale;
	if (!Number.isNaN(minScale) && zoom <= minScale) return minScale;
	return zoom;
}
//#endregion
//#region src/renderer/lib/pinch.utils.ts
const getTouchDistance = (event) => {
	return Math.sqrt((event.touches[0].pageX - event.touches[1].pageX) ** 2 + (event.touches[0].pageY - event.touches[1].pageY) ** 2);
};
const roundNumber = (num, decimal) => {
	return Number(num.toFixed(decimal));
};
const calculatePinchZoom = (contextInstance, currentDistance) => {
	const { touch, setup } = contextInstance;
	const { scale } = setup;
	const [minScale, maxScale] = scale;
	if (!touch.pinchStartScale || touch.pinchStartDistance === null || !currentDistance) throw new Error("Pinch touches distance was not provided");
	if (currentDistance < 0) return contextInstance.transform.scale;
	const scaleDifference = (currentDistance / touch.pinchStartDistance) ** setup.zoomSensitivity * touch.pinchStartScale;
	return checkZoomBounds(roundNumber(scaleDifference, 2), minScale, maxScale);
};
const calculateTouchMidPoint = (instance, event, scale, contentComponent) => {
	const contentRect = contentComponent.getBoundingClientRect();
	const { touches } = event;
	const firstPointX = roundNumber(touches[0].clientX - contentRect.left, 5);
	const firstPointY = roundNumber(touches[0].clientY - contentRect.top, 5);
	const secondPointX = roundNumber(touches[1].clientX - contentRect.left, 5);
	const secondPointY = roundNumber(touches[1].clientY - contentRect.top, 5);
	return {
		x: (firstPointX + secondPointX) / 2,
		y: (firstPointY + secondPointY) / 2
	};
};
//#endregion
//#region src/renderer/PanZoom.ts
const MOUSE_CLICK_MOVE_THRESHOLD = 5;
const MOUSE_LONG_PRESS_DELAY = 1100;
var PanZoom = class extends EventEmitter {
	initialized = false;
	$wrapper = null;
	$zoom = null;
	$move = null;
	transform;
	touch;
	mouse;
	setup;
	flags;
	panning;
	constructor() {
		super();
		this.transform = {
			scale: 1,
			x: 0,
			y: 0
		};
		this.touch = {
			lastTouch: null,
			pinchStartDistance: null,
			lastDistance: null,
			pinchStartScale: null,
			pinchMidpoint: null
		};
		this.mouse = {
			mouseDown: null,
			startX: 0,
			startY: 0,
			maxDistance: 0,
			suppressClick: false,
			holdTimer: null,
			holdActive: false
		};
		this.panning = new Panning(this);
		this.setup = {
			scale: [1, 50],
			zoomSensitivity: 1
		};
		this.flags = {
			useZoom: false,
			haveImageRendering: false
		};
		this.detectFlags();
	}
	initialize($wrapper, $zoom, $move) {
		this.$wrapper = $wrapper;
		this.$zoom = $zoom;
		this.$move = $move;
		this.registerMouseEvents();
		this.registerTouchEvents();
		this.initialized = true;
		if (this.setup.initialTransform) {
			const { x, y, scale } = this.setup.initialTransform;
			this.transform.x = x;
			this.transform.y = y;
			this.transform.scale = scale;
			this.update({ suppressEmit: true });
		}
		this.emit("initialize");
	}
	/**
	* Get scale that would fit the zoom element in the viewport
	* @returns
	*/
	getZoomToFit() {
		const nodeRect = this.$zoom.getBoundingClientRect();
		const nodeWidth = nodeRect.width / this.transform.scale;
		const nodeHeight = nodeRect.height / this.transform.scale;
		const scaleX = this.$wrapper.offsetWidth / nodeWidth;
		const scaleY = this.$wrapper.offsetHeight / nodeHeight;
		return Math.min(scaleX, scaleY);
	}
	_getOffset() {
		const wrapperOffset = this.$wrapper.getBoundingClientRect();
		const contentOffset = this.$zoom.getBoundingClientRect();
		const xOff = wrapperOffset.x * this.transform.scale;
		const yOff = wrapperOffset.y * this.transform.scale;
		return {
			x: (contentOffset.x + xOff) / this.transform.scale,
			y: (contentOffset.y + yOff) / this.transform.scale
		};
	}
	/**
	* Sets transform data
	*
	* @param position
	* @param position.x Transform X
	* @param position.y Transform Y
	* @param position.zoom Zoom scale
	* @param flags
	* @param flags.suppressEmit If true, don't emit a viewport change
	* @returns
	*/
	setPosition({ x, y, zoom }, { suppressEmit } = { suppressEmit: false }) {
		if (!this.initialized) {
			this.setup.initialTransform = {
				x,
				y,
				scale: zoom
			};
			return;
		}
		this.transform.x = x;
		this.transform.y = y;
		this.transform.scale = zoom;
		this.update({ suppressEmit });
	}
	detectFlags() {
		let haveZoomRendering = false;
		let haveImageRendering = false;
		const webkitBased = navigator.userAgent.match(/AppleWebKit/i);
		const iOSSafari = navigator.userAgent.match(/(iPod|iPhone|iPad)/i) && webkitBased;
		const desktopSafari = navigator.userAgent.match(/safari/i) && !navigator.userAgent.match(/chrome/i);
		const msEdge = navigator.userAgent.indexOf("Edge") > -1;
		if (iOSSafari) haveImageRendering = false;
		else if (desktopSafari) {
			haveImageRendering = false;
			haveZoomRendering = true;
		}
		if (msEdge) haveImageRendering = false;
		this.flags.useZoom = haveZoomRendering;
		this.flags.haveImageRendering = haveImageRendering;
	}
	registerTouchEvents() {
		console.debug("[PanZoom] Registering touch events to $wrapper");
		this.$wrapper.addEventListener("touchstart", this._touch_touchstart.bind(this), { passive: false });
		this.$wrapper.addEventListener("touchmove", this._touch_touchmove.bind(this));
		this.$wrapper.addEventListener("touchend", this._touch_touchend.bind(this));
	}
	unregisterTouchEvents() {
		console.debug("[PanZoom] Unregistering touch events to $wrapper");
		this.$wrapper.removeEventListener("touchstart", this._touch_touchstart.bind(this));
		this.$wrapper.removeEventListener("touchmove", this._touch_touchmove.bind(this));
		this.$wrapper.removeEventListener("touchend", this._touch_touchend.bind(this));
	}
	/**
	* Handle touchstart event from touch registrations
	* This needs to be a variable to correctly pass this context
	*
	* @param e
	*/
	_touch_touchstart = (event) => {
		event.preventDefault();
		if (this.touch.lastTouch && +/* @__PURE__ */ new Date() - this.touch.lastTouch < 200 && event.touches.length === 1) this.emit("doubleTap", event);
		else {
			this.touch.lastTouch = +/* @__PURE__ */ new Date();
			const { touches } = event;
			const isPanningAction = touches.length === 1;
			const isPinchAction = touches.length === 2;
			if (isPanningAction) this.panning.start(touches[0].clientX, touches[0].clientY);
			if (isPinchAction) this.onPinchStart(event);
		}
	};
	/**
	* Handle touchmove event from touch registrations
	* This needs to be a variable to correctly pass this context
	*
	* @param e
	*/
	_touch_touchmove = (event) => {
		if (this.panning.active && event.touches.length === 1) {
			event.preventDefault();
			event.stopPropagation();
			const touch = event.touches[0];
			this.panning.move(touch.clientX, touch.clientY);
		} else if (event.touches.length > 1) this.onPinch(event);
	};
	/**
	* Handle touchend event from touch registrations
	* This needs to be a variable to correctly pass this context
	*
	* @param e
	*/
	_touch_touchend = (event) => {
		if (this.touch.lastTouch && this.panning.active) {
			const touch = event.changedTouches[0];
			const dx = Math.abs(this.panning.x - touch.clientX);
			const dy = Math.abs(this.panning.y - touch.clientY);
			if (Date.now() - this.touch.lastTouch < 1100 && dx < 5 && dy < 5) this.emit("click", {
				clientX: touch.clientX,
				clientY: touch.clientY,
				button: "LCLICK",
				alt: false,
				shift: false,
				ctrl: false,
				meta: false
			});
			if (Date.now() - this.touch.lastTouch >= 1100 && dx < 25 && dy < 25) this.emit("longPress", this.panning.x, this.panning.y);
		}
		if (this.panning.active) {
			this.panning.active = false;
			const touch = event.changedTouches[0];
			this.panning.end(touch.clientX, touch.clientY);
		}
	};
	onPinchStart(event) {
		const distance = getTouchDistance(event);
		this.touch.pinchStartDistance = distance;
		this.touch.lastDistance = distance;
		this.touch.pinchStartScale = this.transform.scale;
		this.panning.active = false;
	}
	onPinch(event) {
		event.preventDefault();
		event.stopPropagation();
		const { scale } = this.transform;
		if (this.touch.pinchStartDistance === null) return;
		const el = document.body;
		const midPoint = calculateTouchMidPoint(this, event, scale, el);
		if (!Number.isFinite(midPoint.x) || !Number.isFinite(midPoint.y)) return;
		const currentDistance = getTouchDistance(event);
		const newScale = calculatePinchZoom(this, currentDistance);
		if (newScale === scale) return;
		this.touch.pinchMidpoint = midPoint;
		this.touch.lastDistance = currentDistance;
		if (Debug.flags.enabled("PANZOOM_PINCH_DEBUG_MESSAGES")) {
			Debug.debug("point", midPoint.x, midPoint.y, "midpoint");
			Debug.debug("text", {
				scale: [scale, newScale],
				x: midPoint.x,
				y: midPoint.y,
				tx: this.transform.x,
				ty: this.transform.y,
				xx: midPoint.x * newScale - midPoint.x * scale,
				yy: midPoint.y * newScale - midPoint.y * scale
			});
		}
		if (Debug.flags.enabled("PANZOOM_PINCH_TRANSFORM_1")) {
			this.transform.x = midPoint.x / newScale - midPoint.x / scale;
			this.transform.y = midPoint.y / newScale - midPoint.y / scale;
		}
		if (Debug.flags.enabled("PANZOOM_PINCH_TRANSFORM_2")) {
			this.transform.x = (midPoint.x - this.transform.x) / (newScale - scale);
			this.transform.y = (midPoint.y - this.transform.y) / (newScale - scale);
		}
		this.transform.scale = newScale;
		this.update();
	}
	registerMouseEvents() {
		console.debug("[PanZoom] Registering mouse events to $wrapper & document");
		this.$wrapper.addEventListener("contextmenu", (e) => {
			e.preventDefault();
		});
		this.$wrapper.addEventListener("wheel", this._mouse_wheel, { passive: true });
		this.$wrapper.addEventListener("mousedown", this._mouse_mousedown, { passive: false });
		document.addEventListener("mousemove", this._mouse_mousemove, { passive: false });
		document.addEventListener("mouseup", this._mouse_mouseup, { passive: false });
		document.addEventListener("mousedown", this._mouse_resetClickSuppression, true);
		document.addEventListener("click", this._mouse_click, true);
		document.addEventListener("auxclick", this._mouse_click, true);
	}
	unregisterMouseEvents() {
		console.debug("[PanZoom] Unregistering mouse events to $wrapper & document");
		this.$wrapper.removeEventListener("wheel", this._mouse_wheel);
		this.$wrapper.removeEventListener("mousedown", this._mouse_mousedown);
		document.removeEventListener("mousemove", this._mouse_mousemove);
		document.removeEventListener("mouseup", this._mouse_mouseup);
		document.removeEventListener("mousedown", this._mouse_resetClickSuppression, true);
		document.removeEventListener("click", this._mouse_click, true);
		document.removeEventListener("auxclick", this._mouse_click, true);
	}
	/**
	* Handle the wheel event from the mouse event registration
	* This needs to be a variable to correctly pass this context
	*
	* @param e
	*/
	_mouse_wheel = (e) => {
		const oldScale = this.transform.scale;
		let delta = -e.deltaY;
		switch (e.deltaMode) {
			case WheelEvent.DOM_DELTA_PIXEL:
				delta /= 53;
				break;
			case WheelEvent.DOM_DELTA_LINE:
				delta /= 3;
				break;
			case WheelEvent.DOM_DELTA_PAGE:
				delta = Math.sign(delta);
				break;
		}
		this.nudgeScale(delta / 2 * this.setup.zoomSensitivity);
		const scale = this.transform.scale;
		if (oldScale !== scale) {
			const dx = e.clientX - this.$wrapper.clientWidth / 2;
			const dy = e.clientY - this.$wrapper.clientHeight / 2;
			this.transform.x -= dx / oldScale;
			this.transform.x += dx / scale;
			this.transform.y -= dy / oldScale;
			this.transform.y += dy / scale;
			this.update();
		}
	};
	/**
	* Handle mousedown event from mouse registrations
	* This needs to be a variable to correctly pass this context
	*
	* @param e
	*/
	_mouse_mousedown = (e) => {
		e.preventDefault();
		e.stopPropagation();
		this.mouse.mouseDown = Date.now();
		this.mouse.startX = e.clientX;
		this.mouse.startY = e.clientY;
		this.mouse.maxDistance = 0;
		this.mouse.suppressClick = false;
		this.endInspectHold();
		if (e.button === 0) {
			this.mouse.holdActive = true;
			this.emit("inspectHoldStart");
			this.mouse.holdTimer = setTimeout(() => {
				this.mouse.holdTimer = null;
				if (this.mouse.holdActive && this.mouse.mouseDown !== null && this.mouse.maxDistance < MOUSE_CLICK_MOVE_THRESHOLD) this.emit("inspectHoldReady");
			}, MOUSE_LONG_PRESS_DELAY);
		}
		if (this.panning.enabled) this.panning.start(e.clientX, e.clientY);
	};
	/**
	* Handle mousemove event from mouse registrations
	* This needs to be a variable to correctly pass this context
	*
	* @param e
	*/
	_mouse_mousemove = (e) => {
		this.updateMouseDistance(e.clientX, e.clientY);
		if (this.mouse.holdActive && this.mouse.maxDistance >= MOUSE_CLICK_MOVE_THRESHOLD) this.endInspectHold();
		if (this.panning.active) {
			e.preventDefault();
			e.stopPropagation();
			if (this.mouse.maxDistance >= MOUSE_CLICK_MOVE_THRESHOLD) this.panning.move(e.clientX, e.clientY);
		} else this.emit("hover", {
			clientX: e.clientX,
			clientY: e.clientY,
			target: e.target
		});
	};
	/**
	* Handle mouseup event from mouse registrations
	* This needs to be a variable to correctly pass this context
	*
	* @param e
	*/
	_mouse_mouseup = (e) => {
		const mouseDown = this.mouse.mouseDown;
		if (mouseDown === null) return;
		this.updateMouseDistance(e.clientX, e.clientY);
		const elapsed = Date.now() - mouseDown;
		const didDrag = this.mouse.maxDistance >= MOUSE_CLICK_MOVE_THRESHOLD;
		const didLongPress = e.button === 0 && elapsed >= 1100 && !didDrag;
		this.mouse.suppressClick = didDrag || didLongPress;
		if (elapsed < 1100 && !didDrag) this.emit("click", {
			button: [
				"LCLICK",
				"MCLICK",
				"RCLICK"
			][e.button],
			clientX: e.clientX,
			clientY: e.clientY,
			alt: e.altKey,
			ctrl: e.ctrlKey,
			meta: e.metaKey,
			shift: e.shiftKey
		});
		else if (didLongPress) this.emit("longPress", this.mouse.startX, this.mouse.startY);
		this.endInspectHold();
		this.mouse.mouseDown = null;
		if (this.panning.active) {
			e.preventDefault();
			e.stopPropagation();
			if (didDrag) this.panning.end(e.clientX, e.clientY);
			else this.panning.active = false;
		}
	};
	updateMouseDistance(clientX, clientY) {
		if (this.mouse.mouseDown === null) return;
		const distance = Math.max(Math.abs(clientX - this.mouse.startX), Math.abs(clientY - this.mouse.startY));
		this.mouse.maxDistance = Math.max(this.mouse.maxDistance, distance);
	}
	endInspectHold() {
		if (this.mouse.holdTimer !== null) {
			clearTimeout(this.mouse.holdTimer);
			this.mouse.holdTimer = null;
		}
		if (!this.mouse.holdActive) return;
		this.mouse.holdActive = false;
		this.emit("inspectHoldEnd");
	}
	_mouse_resetClickSuppression = () => {
		this.mouse.suppressClick = false;
	};
	_mouse_click = (e) => {
		if (!this.mouse.suppressClick) return;
		this.mouse.suppressClick = false;
		e.preventDefault();
		e.stopImmediatePropagation();
	};
	/**
	* Update viewport scale and position
	*
	* @param flags
	* @param flags.suppressEmit Do not emit viewportMove
	*/
	update({ suppressEmit } = { suppressEmit: false }) {
		if (this.flags.useZoom) this.$zoom.style.setProperty("zoom", this.transform.scale * 100 + "%");
		else this.$zoom.style.setProperty("transform", `scale(${this.transform.scale})`);
		this.$move.style.setProperty("transform", `translate(${this.transform.x}px, ${this.transform.y}px)`);
		if (!suppressEmit) this.emit("viewportMove", {
			scale: this.transform.scale,
			x: this.transform.x,
			y: this.transform.y
		});
	}
	cleanup() {
		this.endInspectHold();
		this.unregisterTouchEvents();
		this.unregisterMouseEvents();
	}
	nudgeScale(adj) {
		this.transform.scale = checkZoomBounds(this.transform.scale * 1.5 ** adj, this.setup.scale[0], this.setup.scale[1]);
	}
};
//#endregion
//#region src/renderer/RendererContext.tsx
const RendererContext = createContext(null);
//#endregion
//#region src/renderer/PanZoomWrapper.tsx
const PanZoomWrapper = ({ children, initialPosition }) => {
	const wrapper = useRef(null);
	const zoom = useRef(null);
	const move = useRef(null);
	const instance = useRef(new PanZoom()).current;
	useEffect(() => {
		const $wrapper = wrapper.current;
		const $zoom = zoom.current;
		const $move = move.current;
		if ($wrapper && $zoom && $move) {
			if (initialPosition) {
				const pos = initialPosition(instance.flags.useZoom);
				if (pos) instance.setPosition({
					x: pos.x,
					y: pos.y,
					zoom: pos.zoom || 0
				}, { suppressEmit: true });
				else console.warn("Failed to set position via initialPosition; no pos");
			}
			instance.initialize($wrapper, $zoom, $move);
		}
		return () => {
			instance.cleanup();
		};
	}, []);
	return /* @__PURE__ */ React.createElement(RendererContext.Provider, { value: instance }, /* @__PURE__ */ React.createElement("div", {
		ref: wrapper,
		className: "board-wrapper",
		style: { touchAction: "none" }
	}, /* @__PURE__ */ React.createElement("div", {
		ref: zoom,
		className: "board-zoom"
	}, /* @__PURE__ */ React.createElement("div", {
		ref: move,
		className: "board-move"
	}, children))));
};
//#endregion
export { MOUSE_LONG_PRESS_DELAY, PanZoom, PanZoomWrapper, RendererContext };

//# sourceMappingURL=renderer.mjs.map